/** 속견표 — UNIPASS 001Q 풍 위치격자(행 0~90 × 열 0~9).
 *  류(2자리) 그리드 → 류 클릭 시 ⭐하위 속견표(그 류의 호 4자리 격자)로 진입.
 *  내용(류/호명) 중심, 부별 tint, 국문/EN 토글, 분류사례 배지. 호 클릭 → onHeadingClick(hs4).
 */
import React, { useState, useEffect, useMemo } from "react";
import {
  CHAPTERS_97,
  CHAPTERS_97_EN,
  AUDIT_SUPPORTED,
  SECTIONS,
  SECTION_STRIPE,
  SECTION_DESC,
  SECTION_RANGE,
  SECTION_PRINCIPLE,
} from "./chaptersData";
import "./theme.css";

const NAME_BY_CH = Object.fromEntries(CHAPTERS_97.map((c) => [c.ch, c.name]));
const ROWS = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90];
const COLS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

// 분류사례 heat 단일 색(accent) — 류·호 격자 공통. 사례 많을수록 진하게.
const HEAT_BASE = "#1a73e8";

// 부(部) 시작 류만 띠+라벨 — 모든 셀에 띠를 넣으면 화려해서, 부가 바뀌는 첫 류에만 표시.
const SEC_START = {};
SECTIONS.forEach((s, i) => {
  const first = (s.chapters || [])[0];
  if (first) SEC_START[first] = { sec: s.sec, color: SECTION_STRIPE[i] };
});

export default function QuickReferenceGrid({
  onHeadingClick,
  // 선택적 controlled props — Master-Detail 셸이 좌측 네비게이터로 제어. 없으면 내부 state.
  q: qProp,
  onQChange,
  lang: langProp,
  onLangChange,
  subCh: subChProp,
  onSubChChange,
  hideControls = false, // 셸이 검색/언어 토글을 좌측에서 제공 → 헤더 컨트롤 숨김
  isMobile = false, // 모바일: 호 격자 대신 세로 리스트
}) {
  const [qLocal, setQLocal] = useState("");
  const [langLocal, setLangLocal] = useState("Kor");
  const [subChLocal, setSubChLocal] = useState(null);
  const q = qProp !== undefined ? qProp : qLocal;
  const setQ = onQChange || setQLocal;
  const lang = langProp !== undefined ? langProp : langLocal;
  const setLang = onLangChange || setLangLocal;
  const subCh = subChProp !== undefined ? subChProp : subChLocal; // null=류 그리드 / "84"=84류 하위
  const setSubCh = onSubChChange || setSubChLocal;
  const [stats, setStats] = useState({});
  const [sub, setSub] = useState({ loading: false, headings: [] });
  const [topCases, setTopCases] = useState([]); // 이 류의 주요 분류사례 Top 5

  useEffect(() => {
    fetch("/api/onto/chapter-stats")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setStats(d.stats || {}))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!subCh) {
      setSub({ loading: false, headings: [] });
      return;
    }
    setSub({ loading: true, headings: [] });
    fetch(`/api/onto/chapter-headings?chapter=${subCh}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setSub({ loading: false, headings: d.headings || [] }))
      .catch(() => setSub({ loading: false, headings: [] }));
    setTopCases([]);
    fetch(`/api/onto/chapter-top-cases?chapter=${subCh}&limit=5`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setTopCases(d.cases || []))
      .catch(() => setTopCases([]));
  }, [subCh]);

  // 호 목록 — 번호순 정렬 후 빈칸 없이 순차 패킹(CLHS식). 위치 산포 X.
  const sortedHeadings = useMemo(() => {
    return (sub.headings || [])
      .map((h) => ({
        hs4: h.hs4,
        ko: h.title_ko || h.name || "",
        en: h.title_en || "",
        pc: h.case_count || 0,
      }))
      .sort((a, b) => String(a.hs4).localeCompare(String(b.hs4)));
  }, [sub.headings]);

  // 호별 최대 분류사례 — 셀 색 농도(heat) 정규화용.
  const maxPc = useMemo(
    () => sortedHeadings.reduce((m, h) => Math.max(m, h.pc || 0), 0),
    [sortedHeadings],
  );
  // 류별 최대 분류사례 — 류 격자 heat 정규화용.
  const maxStat = useMemo(
    () => Object.values(stats).reduce((m, n) => Math.max(m, n || 0), 0),
    [stats],
  );

  // 10열 단위로 행 분할(패킹 격자).
  const subRows = useMemo(() => {
    const rows = [];
    for (let i = 0; i < sortedHeadings.length; i += COLS.length) {
      rows.push(sortedHeadings.slice(i, i + COLS.length));
    }
    return rows;
  }, [sortedHeadings]);

  const query = q.trim();
  const matchTxt = (num, ko, en) => {
    if (!query) return false;
    if (/[0-9]/.test(query)) return num.includes(query.replace(/[^0-9]/g, ""));
    const lq = query.toLowerCase();
    return (ko && ko.includes(query)) || (en && en.toLowerCase().includes(lq));
  };

  // 공통 셀 렌더
  const Cell = ({
    num,
    ko,
    en,
    tint,
    stripe,
    secLabel,
    supported,
    pc,
    onClick,
    isHeading,
  }) => {
    const hit = matchTxt(num, ko, en);
    const raw = lang === "En" ? en || ko : ko;
    const display = isHeading ? shortName(raw) : raw;
    return (
      <td style={{ padding: 0, border: "1px solid var(--audit-border)" }}>
        <button
          onClick={onClick}
          title={`${isHeading ? fmtHs4(num) : num} ${ko}${pc ? ` · 분류사례 ${pc.toLocaleString()}건` : ""}`}
          style={{
            ...cellBtn,
            paddingLeft: stripe ? 11 : 8,
            background: hit ? "var(--audit-accent-bg)" : tint,
            outline: hit ? "2px solid var(--audit-accent)" : "none",
            outlineOffset: -2,
          }}
          onMouseEnter={(e) => {
            if (!hit) e.currentTarget.style.filter = "brightness(0.96)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.filter = "none";
          }}
        >
          {stripe && (
            <span
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: 4,
                background: stripe,
              }}
            />
          )}
          {!isHeading && (
            <span style={drill} title="클릭 → 호 속견표">
              ⊞
            </span>
          )}
          <span style={cellMeta}>
            <span style={chNum}>
              {isHeading ? fmtHs4(num) : num}
              {secLabel && <span style={secTag}>{secLabel}</span>}
            </span>
            <span style={cellMetaRight}>
              {supported && <span style={dot} title="AI Audit 지원" />}
              {pc != null && (
                <span
                  style={pcBadge}
                  title={`분류사례 ${Number(pc).toLocaleString()}건`}
                >
                  {pc >= 1000 ? `${Math.round(pc / 1000)}k` : pc}
                </span>
              )}
            </span>
          </span>
          <span style={{ ...chName, fontSize: lang === "En" ? 12 : 12.5 }}>
            {display}
          </span>
        </button>
      </td>
    );
  };

  const inSub = !!subCh;
  const subTint = "#fbfbfc"; // heat 0건 배경(거의 흰색)
  const subBaseColor = HEAT_BASE; // 호 격자도 동일 accent heat

  return (
    <div className="audit-root" style={{ marginBottom: 20 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <h2
          style={{
            fontSize: 13,
            color: "var(--audit-text-muted)",
            textTransform: "uppercase",
            letterSpacing: 0.5,
            margin: 0,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          {inSub ? (
            <>
              <button
                onClick={() => setSubCh(null)}
                style={{
                  border: "1px solid var(--audit-border)",
                  background: "var(--audit-bg)",
                  borderRadius: 6,
                  padding: "4px 9px",
                  fontSize: 12,
                  cursor: "pointer",
                  color: "var(--audit-text)",
                  textTransform: "none",
                }}
              >
                ← 속견표
              </button>
              <span style={{ textTransform: "none" }}>
                제{parseInt(subCh, 10)}류 {NAME_BY_CH[subCh]} ·{" "}
                <span style={{ fontWeight: 400 }}>호(4자리) 속견표</span>
              </span>
            </>
          ) : null}
        </h2>
        {!hideControls && (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <div
              style={{
                display: "inline-flex",
                border: "1px solid var(--audit-border)",
                borderRadius: 7,
                overflow: "hidden",
              }}
            >
              {[
                ["Kor", "국문"],
                ["En", "EN"],
              ].map(([v, label]) => (
                <button
                  key={v}
                  onClick={() => setLang(v)}
                  style={{
                    border: "none",
                    padding: "6px 12px",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                    background:
                      lang === v ? "var(--audit-accent)" : "var(--audit-bg)",
                    color: lang === v ? "#fff" : "var(--audit-text-muted)",
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={
                inSub ? "호명/번호 검색" : "류명/번호 검색 (예: 가죽, 85)"
              }
              style={{
                fontSize: 12.5,
                padding: "7px 10px",
                borderRadius: 7,
                border: "1px solid var(--audit-border)",
                background: "var(--audit-bg)",
                color: "var(--audit-text)",
                width: 200,
                maxWidth: "45vw",
              }}
            />
          </div>
        )}
      </div>

      {inSub && sub.loading && (
        <div
          style={{
            padding: 24,
            color: "var(--audit-text-muted)",
            fontSize: 13,
          }}
        >
          호 목록 불러오는 중…
        </div>
      )}

      {/* 하위 속견표 (모바일) — 호(4자리) 세로 리스트. 격자 대신 엄지 탐색용. */}
      {inSub && !sub.loading && isMobile && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {sortedHeadings.length === 0 ? (
            <div
              style={{
                padding: 16,
                color: "var(--audit-text-muted)",
                fontSize: 13,
              }}
            >
              이 류의 호 정보가 없습니다.
            </div>
          ) : (
            sortedHeadings.map((h) => (
              <button
                key={h.hs4}
                onClick={() => onHeadingClick?.(h.hs4, subCh, h.ko)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  width: "100%",
                  textAlign: "left",
                  border: "1px solid var(--audit-border)",
                  borderRadius: 8,
                  padding: "11px 12px",
                  background: heatBg(h.pc, maxPc, subBaseColor, subTint),
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                <span
                  style={{
                    fontFamily: "JetBrains Mono, monospace",
                    fontSize: 13,
                    fontWeight: 700,
                    color: "var(--audit-text)",
                    flexShrink: 0,
                    minWidth: 46,
                  }}
                >
                  {fmtHs4(h.hs4)}
                </span>
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: 13.5,
                    color: "var(--audit-text)",
                    lineHeight: 1.35,
                  }}
                >
                  {lang === "En" ? h.en || h.ko : h.ko}
                </span>
                {h.pc > 0 && (
                  <span
                    style={{
                      flexShrink: 0,
                      fontSize: 11,
                      color: "var(--audit-text-muted)",
                      fontWeight: 600,
                    }}
                  >
                    사례 {h.pc >= 1000 ? `${Math.round(h.pc / 1000)}k` : h.pc}
                  </span>
                )}
                <span
                  style={{
                    flexShrink: 0,
                    color: "var(--audit-text-faint)",
                    fontSize: 16,
                  }}
                >
                  ›
                </span>
              </button>
            ))
          )}
        </div>
      )}

      {/* 하위 속견표 (데스크톱) — 호(4자리) 번호순 패킹 격자 (빈칸 없음, CLHS식) */}
      {inSub && !sub.loading && !isMobile && (
        <div style={{ overflowX: "auto" }}>
          {sortedHeadings.length === 0 ? (
            <div
              style={{
                padding: 24,
                color: "var(--audit-text-muted)",
                fontSize: 13,
              }}
            >
              이 류의 호 정보가 없습니다.
            </div>
          ) : (
            <table
              style={{
                borderCollapse: "collapse",
                width: "100%",
                tableLayout: "fixed",
                minWidth: 880,
              }}
            >
              <tbody>
                {subRows.map((row, ri) => (
                  <tr key={ri}>
                    {row.map((h) => (
                      <Cell
                        key={h.hs4}
                        num={h.hs4}
                        ko={h.ko}
                        en={h.en}
                        tint={heatBg(h.pc, maxPc, subBaseColor, subTint)}
                        pc={h.pc > 0 ? h.pc : undefined}
                        onClick={() => onHeadingClick?.(h.hs4, subCh, h.ko)}
                        isHeading
                      />
                    ))}
                    {/* 마지막 행 빈 칸 채우기(격자 폭 유지) */}
                    {row.length < COLS.length &&
                      Array.from({ length: COLS.length - row.length }).map(
                        (_, k) => (
                          <td
                            key={`pad${k}`}
                            style={{ background: "transparent" }}
                          />
                        ),
                      )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* 류(2자리) — 위치 격자 (행 10단위 × 열 1단위) */}
      {!inSub && (
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              borderCollapse: "collapse",
              width: "100%",
              tableLayout: "fixed",
              minWidth: 880,
            }}
          >
            <thead>
              <tr>
                <th style={{ width: 26 }} />
                {COLS.map((c) => (
                  <th key={c} style={headTh}>
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map((r) => (
                <tr key={r}>
                  <th style={rowTh}>{r}</th>
                  {COLS.map((c) => {
                    const n = r + c;
                    if (n < 1 || n > 97)
                      return (
                        <td key={c} style={{ background: "transparent" }} />
                      );
                    const ch = String(n).padStart(2, "0");
                    const name = NAME_BY_CH[ch];
                    if (!name)
                      return (
                        <td key={c} style={{ background: "transparent" }}>
                          <div style={reservedBox}>
                            <span style={{ fontSize: 11 }}>{ch}</span>
                            <span style={{ fontSize: 11 }}>(유보)</span>
                          </div>
                        </td>
                      );
                    return (
                      <Cell
                        key={c}
                        num={ch}
                        ko={name}
                        en={CHAPTERS_97_EN[ch]}
                        tint={heatBg(
                          stats[ch] || 0,
                          maxStat,
                          HEAT_BASE,
                          "#fbfbfc",
                        )}
                        stripe={
                          SEC_START[ch] ? "var(--audit-border-strong)" : null
                        }
                        secLabel={SEC_START[ch] && SEC_START[ch].sec}
                        supported={AUDIT_SUPPORTED.has(ch)}
                        pc={stats[ch]}
                        onClick={() => setSubCh(ch)}
                      />
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div
        style={{ fontSize: 12, color: "var(--audit-text-muted)", marginTop: 8 }}
      >
        {inSub ? (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <span>번호순 정렬. 호(4자리) 클릭 → 소호·세번 상세로 이동.</span>
            {maxPc > 0 && (
              <span
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                <span style={{ color: "var(--audit-text-faint)" }}>
                  분류사례 적음
                </span>
                <span
                  style={{
                    width: 80,
                    height: 11,
                    borderRadius: 3,
                    border: "1px solid var(--audit-border)",
                    background: `linear-gradient(90deg, ${heatBg(1, 100, subBaseColor, subTint)}, ${heatBg(100, 100, subBaseColor, subTint)})`,
                  }}
                />
                <span style={{ color: "var(--audit-text-faint)" }}>
                  많음 (최대 {maxPc.toLocaleString()}건)
                </span>
              </span>
            )}
          </span>
        ) : (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <span>
              행(10단위)+열(1단위)=류 번호. 색 진하기=분류사례 양. 좌측 회색
              띠+‘N부’=부(部) 시작. <span style={dot2} />
              =AI Audit({AUDIT_SUPPORTED.size}개).{" "}
              <span style={{ color: "var(--audit-accent)" }}>⊞</span>=클릭하면
              그 류의 호(4자리) 속견표.
            </span>
            {maxStat > 0 && (
              <span
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                <span style={{ color: "var(--audit-text-faint)" }}>
                  사례 적음
                </span>
                <span
                  style={{
                    width: 80,
                    height: 11,
                    borderRadius: 3,
                    border: "1px solid var(--audit-border)",
                    background: `linear-gradient(90deg, ${heatBg(1, 100, HEAT_BASE, "#fbfbfc")}, ${heatBg(100, 100, HEAT_BASE, "#fbfbfc")})`,
                  }}
                />
                <span style={{ color: "var(--audit-text-faint)" }}>
                  많음 (최대 {maxStat.toLocaleString()}건)
                </span>
              </span>
            )}
          </span>
        )}
      </div>

      {inSub && topCases.length > 0 && (
        <div style={topCasesWrap}>
          <div style={topCasesTitle}>
            제{parseInt(subCh, 10)}류 주요 분류사례
            <span style={{ color: "var(--audit-text-faint)", fontWeight: 400 }}>
              {" "}
              · 공식 결정례 우선 · 최신순
            </span>
          </div>
          <div>
            {topCases.map((c, i) => {
              const nm = cleanTitle(c.title);
              const full = String(c.title || "").trim();
              const showRaw = full && full !== nm; // 정리로 빠진 게 있으면 원문 노출
              return (
                <button
                  key={c.case_id || i}
                  onClick={() => c.hs4 && onHeadingClick?.(c.hs4, subCh)}
                  style={topCaseRow}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "var(--audit-bg-hover)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "transparent")
                  }
                >
                  <div style={topCaseLine1}>
                    <span style={topCaseRank}>{i + 1}</span>
                    <span style={topCaseTitle}>{nm}</span>
                    <span style={srcBadge(c.source)}>{srcLabel(c.source)}</span>
                    <span style={topCaseHs}>{fmtHsk(c.hsk || c.hs6)}</span>
                    {c.decision_date && (
                      <span style={topCaseDate}>
                        {String(c.decision_date).slice(0, 10)}
                      </span>
                    )}
                  </div>
                  {showRaw && (
                    <div style={topCaseRaw}>
                      <span style={{ color: "var(--audit-text-faint)" }}>
                        원문:{" "}
                      </span>
                      {full}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          <div style={topCasesHint}>
            품목명은 읽기 쉽게 정리하되 <b>관세청 원문을 그대로</b> 함께
            표시합니다. 행 클릭 → 해당 호(4자리) 상세로 이동.
          </div>
        </div>
      )}

      {!inSub && (
        <div style={legendWrap}>
          <div style={legendTitle}>부(部) 분류 체계 — 류가 어떻게 묶이나</div>
          <p style={legendPrinciple}>{SECTION_PRINCIPLE}</p>
          <div style={legendGrid}>
            {SECTIONS.map((s, i) => (
              <div key={s.sec} style={legendItem}>
                <span style={legendLabel}>
                  <span style={{ display: "block" }}>
                    <b style={{ fontWeight: 700 }}>{s.sec}</b> {s.name}{" "}
                    <span style={{ color: "var(--audit-text-faint)" }}>
                      ({SECTION_RANGE[i]})
                    </span>
                  </span>
                  <span style={legendDesc}>{SECTION_DESC[i]}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function fmtHs4(hs4) {
  const s = String(hs4 || "");
  return s.length === 4 ? `${s.slice(0, 2)}.${s.slice(2)}` : s;
}

// hex → {r,g,b}
function hexRgb(hex) {
  const h = String(hex).replace("#", "");
  const n =
    h.length === 3
      ? h
          .split("")
          .map((c) => c + c)
          .join("")
      : h;
  const i = parseInt(n, 16);
  return { r: (i >> 16) & 255, g: (i >> 8) & 255, b: i & 255 };
}

// 분류사례 건수 heat — 매일 보는 화면이라 저강도: 대부분 흰색에 가깝고 상위 소수만 도드라짐.
// pow(ratio,1.4)로 저·중간값은 거의 흰색, 최대 alpha 0.5로 peak 도 은은하게.
function heatBg(pc, maxPc, baseHex, zeroTint) {
  if (!pc || pc <= 0 || !maxPc) return zeroTint;
  const ratio = Math.min(1, pc / maxPc);
  const alpha = 0.5 * Math.pow(ratio, 1.4);
  if (alpha < 0.015) return zeroTint;
  const { r, g, b } = hexRgb(baseHex);
  return `rgba(${r},${g},${b},${alpha.toFixed(3)})`;
}

// HSK 표시 — 10자리는 점 구분(8536.41.0000), 6자리는 8536.41
function fmtHsk(code) {
  const s = String(code || "");
  if (s.length === 10) return `${s.slice(0, 4)}.${s.slice(4, 6)}.${s.slice(6)}`;
  if (s.length === 6) return `${s.slice(0, 4)}.${s.slice(4)}`;
  if (s.length === 4) return `${s.slice(0, 2)}.${s.slice(2)}`;
  return s;
}

// 통관 품목명 정리 — "GRIDDLE; AEB761247; 277.4X550; CN;" → "GRIDDLE".
// ';' 구분 세그먼트 중 모델코드/치수/국가코드를 버리고 사람이 읽는 이름만 추출.
function cleanTitle(raw) {
  if (!raw) return "";
  const segs = String(raw)
    .split(/[;|]/)
    .map((s) => s.trim())
    .filter(Boolean);
  const isCode = (s) => {
    if (/^[A-Z]{2,3}$/.test(s)) return true; // 국가·약어 (CN, US)
    if (/^[\d.]+\s*[xX×*]\s*[\d.]+/.test(s)) return true; // 치수 277.4X550
    if (/\s/.test(s)) return false; // 공백 있으면 이름일 가능성 높음
    if (/\d/.test(s) && /^[A-Za-z0-9\-./%()+]+$/.test(s)) return true; // 단일토큰+숫자 = 코드/스펙
    return false;
  };
  const hasLetters = (s) => /[A-Za-z가-힣]{2,}/.test(s);
  const name = segs.find((s) => hasLetters(s) && !isCode(s));
  let out = (name || segs[0] || String(raw)).trim();
  out = out
    .replace(/[;,\-\s]+$/, "")
    .replace(/\s{2,}/g, " ")
    .trim();
  return out || String(raw).trim();
}

function srcLabel(s) {
  if (!s) return "분류사례";
  if (s === "pre_ruling") return "품목분류 결정례";
  if (s.startsWith("clip")) return "관세청 CLIP";
  if (s.startsWith("bigdata")) return "통관 실적";
  return s;
}
// 공식 결정례·CLIP 은 accent, 통관실적 등은 회색 배지
function srcBadge(s) {
  const official = s === "pre_ruling" || (s && s.startsWith("clip"));
  return {
    flexShrink: 0,
    fontSize: 10.5,
    fontWeight: 600,
    padding: "1px 7px",
    borderRadius: 10,
    whiteSpace: "nowrap",
    background: official ? "var(--audit-accent)" : "var(--audit-bg-subtle)",
    color: official ? "#fff" : "var(--audit-text-muted)",
    border: official ? "none" : "1px solid var(--audit-border)",
  };
}

// 호 명칭 단축 — 긴 호해설 → 짧은 라벨(괄호·단서절 제거, 길이 cap). 균일 격자용.
function shortName(s) {
  if (!s) return "";
  let t = String(s)
    .replace(/\([^)]*\)/g, "")
    .replace(/\[[^\]]*\]/g, "")
    .trim();
  t = t
    .split(
      /(?:으로 한정한다|로 한정한다|는 제외한다|을 제외한다|를 제외한다|에 해당하는)/,
    )[0]
    .trim();
  if (t.length > 24) t = t.slice(0, 23).trim() + "…";
  return t || String(s).slice(0, 23);
}

const headTh = {
  fontSize: 11.5,
  fontWeight: 700,
  color: "var(--audit-text-faint)",
  textAlign: "center",
  paddingBottom: 2,
};
const rowTh = {
  fontSize: 11.5,
  fontWeight: 700,
  color: "var(--audit-text-faint)",
  textAlign: "center",
  width: 26,
};
const reservedBox = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  minHeight: 54,
  color: "var(--audit-text-faint)",
  border: "1px dashed var(--audit-border)",
};
const cellBtn = {
  position: "relative",
  width: "100%",
  height: 64,
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  justifyContent: "flex-start",
  gap: 2,
  padding: "5px 8px",
  border: "none",
  cursor: "pointer",
  fontFamily: "inherit",
  textAlign: "left",
  overflow: "hidden",
  transition: "filter 0.1s",
};
const cellMeta = {
  width: "100%",
  minWidth: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 4,
  flexShrink: 0,
};
const cellMetaRight = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: 5,
  minWidth: 0,
  flexShrink: 0,
};
const chNum = {
  fontSize: 11.5,
  lineHeight: "14px",
  flexShrink: 0,
  fontWeight: 500,
  color: "var(--audit-text-faint)",
  letterSpacing: 0.2,
  fontFamily: "JetBrains Mono, monospace",
};
// 부(部) 시작 류에만 붙는 작은 라벨 (예: "6부")
const secTag = {
  marginLeft: 5,
  fontSize: 11.5,
  lineHeight: "14px",
  fontWeight: 700,
  color: "var(--audit-text-muted)",
  fontFamily: "inherit",
};
const chName = {
  fontWeight: 600,
  color: "var(--audit-text)",
  lineHeight: "16px",
  flexShrink: 0,
  wordBreak: "keep-all",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
};
const drill = {
  position: "absolute",
  bottom: 3,
  right: 5,
  fontSize: 11,
  color: "var(--audit-accent)",
  opacity: 0.5,
  lineHeight: 1,
};
const dot = {
  display: "inline-block",
  width: 6,
  height: 6,
  flexShrink: 0,
  borderRadius: "50%",
  background: "var(--audit-accent)",
};
const pcBadge = {
  fontSize: 11.5,
  lineHeight: "14px",
  flexShrink: 0,
  color: "var(--audit-text-faint)",
  fontWeight: 500,
  fontFamily: "JetBrains Mono, monospace",
};
const dot2 = {
  display: "inline-block",
  width: 6,
  height: 6,
  borderRadius: "50%",
  background: "var(--audit-accent)",
  verticalAlign: "middle",
};
const legendWrap = {
  marginTop: 14,
  padding: "12px 14px",
  border: "1px solid var(--audit-border)",
  borderRadius: 8,
  background: "var(--audit-bg-subtle)",
};
const legendTitle = {
  fontSize: 12.5,
  fontWeight: 700,
  color: "var(--audit-text)",
  marginBottom: 8,
};
const legendPrinciple = {
  fontSize: 12,
  lineHeight: 1.65,
  color: "var(--audit-text-muted)",
  margin: "0 0 12px",
};
const legendGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(290px, 1fr))",
  gap: "10px 16px",
};
const legendItem = {
  display: "flex",
  alignItems: "flex-start",
  gap: 9,
  minWidth: 0,
};
const legendLabel = {
  fontSize: 12,
  color: "var(--audit-text)",
  minWidth: 0,
};
const legendDesc = {
  display: "block",
  fontSize: 11,
  lineHeight: 1.45,
  color: "var(--audit-text-muted)",
  marginTop: 1,
};

// 주요 분류사례 Top 5 위젯
const topCasesWrap = {
  marginTop: 14,
  padding: "12px 14px",
  border: "1px solid var(--audit-border)",
  borderRadius: 8,
  background: "var(--audit-bg-elevated)",
};
const topCasesTitle = {
  fontSize: 12.5,
  fontWeight: 700,
  color: "var(--audit-text)",
  marginBottom: 8,
};
const topCaseRow = {
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  gap: 3,
  width: "100%",
  border: "none",
  borderBottom: "1px solid var(--audit-border)",
  background: "transparent",
  padding: "8px 6px",
  cursor: "pointer",
  fontFamily: "inherit",
  textAlign: "left",
  transition: "background 0.1s",
};
const topCaseLine1 = {
  display: "flex",
  alignItems: "center",
  gap: 9,
  width: "100%",
};
const topCaseRaw = {
  paddingLeft: 27,
  fontSize: 11,
  lineHeight: 1.4,
  color: "var(--audit-text-muted)",
  wordBreak: "break-word",
};
const topCaseRank = {
  flexShrink: 0,
  width: 18,
  height: 18,
  borderRadius: "50%",
  background: "var(--audit-bg-subtle)",
  color: "var(--audit-text-muted)",
  fontSize: 11,
  fontWeight: 700,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};
const topCaseTitle = {
  flex: 1,
  minWidth: 0,
  fontSize: 12.5,
  color: "var(--audit-text)",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};
const topCaseHs = {
  flexShrink: 0,
  fontFamily: "JetBrains Mono, monospace",
  fontSize: 11.5,
  fontWeight: 600,
  color: "var(--audit-accent-dark)",
};
const topCaseDate = {
  flexShrink: 0,
  fontSize: 10.5,
  color: "var(--audit-text-faint)",
  fontFamily: "JetBrains Mono, monospace",
  width: 76,
  textAlign: "right",
};
const topCasesHint = {
  fontSize: 11,
  color: "var(--audit-text-faint)",
  marginTop: 8,
};
